import Vue, { unref, ref, computed, getCurrentInstance } from "vue";
import Toast from "vue-toastification";
import "@/css/toastStyle.css";
// eslint-disable-next-line import/no-extraneous-dependencies, import/no-unresolved
import { asyncComputed } from "@vueuse/core";
import { usePersistedValues } from "./usePersistedValues";
import VToastComponent from "../components/raft/VToastComponent.vue";

const { setPersistedValue, getPersistedValue } = usePersistedValues({
  route: "*",
  module: "*",
});

const fetchBaseline = async () => {
  const historyRef = await getPersistedValue("notificationHistory");
  if (historyRef.value === null) {
    await setPersistedValue({ key: "notificationHistory", newValue: [] });
  }
  if (historyRef.value) {
    return historyRef.value;
  }
};

const history = asyncComputed(
  async () => unref(await fetchBaseline()),
  [], // initial state
);

const set = (newValue) =>
  setPersistedValue({
    key: "notificationHistory",
    newValue,
  });

// eslint-disable-next-line no-unused-vars
let router = null;

let toastOptions = {
  timeout: 12000,
  draggable: false,
  position: "bottom-right",
  pauseOnHover: true,
  showCloseButtonOnHover: true,
  closeOnClick: false,
  hideProgressBar: true,
  accessibility: {
    toastRole: "alert",
    closeButtonLabel: "close",
  },
  transition: "Vue-Toastification__fade",
  shareAppContext: true,
};

Vue.use(Toast, toastOptions);

const $toast = Vue.prototype.$toast;

function addToHistory(
  type,
  message,
  destination,
  metadata,
  date,
  id,
  unread = true,
) {
  let modified = history.value;
  if (!modified?.length) {
    modified = [];
  }
  modified.push({ type, message, destination, metadata, id, unread, date });
  set(modified);
}

const newSortableRandomId = () =>
  Date.now().toString(36) + Math.random().toString(36).substring(2);

function showNotification(
  type,
  message,
  destination = "",
  metadata = {},
  date = Date.now(),
) {
  const id = newSortableRandomId();
  addToHistory(type, message, destination, metadata, date, id);
  const component = typeof message === "string" ? VToastComponent : message;
  const content = {
    component,
    props: {
      message,
      destination,
      metadata,
    },
  };
  if (type === "warn") {
    $toast.warning(content, { ...toastOptions, id });
  } else if (type === "success") {
    $toast.success(content, { ...toastOptions, id });
  } else if (type === "error") {
    $toast.error(content, { ...toastOptions, id });
  } else if (type === "persist-info") {
    $toast(content, { ...toastOptions, id, timeout: false });
  } else if (type === "persist-warn") {
    $toast.warning(content, { ...toastOptions, id, timeout: false });
  } else if (type === "persist-success") {
    $toast.success(content, { ...toastOptions, id, timeout: false });
  } else if (type === "persist-error") {
    $toast.error(content, { ...toastOptions, id, timeout: false });
  } else {
    $toast(content, { ...toastOptions, id });
  }
}

function clearNotification() {
  $toast.clear();
}

function readNotification(id) {
  const modified = history.value;
  if (modified?.length > 0) {
    const index = history.value?.findIndex(
      (notification) => notification.id === id,
    );
    modified[index].unread = false;
    set(modified);
  }
}

function dsmHide() {
  document.querySelector("#dsm-messages")?.classList.add("hidden");
}

function dsmCapture() {
  if (window.SCISHIELD_DSM_CAPTURE) {
    const items = document.querySelectorAll("#dsm-messages div ul li");
    const statusItem = document.querySelector("#dsm-messages div");
    const status = statusItem?.classList.contains("error") ? "error" : "info";
    if (window.SCISHIELD_DSM_NOTIFY) {
      dsmHide();
    }
    items.forEach((item) => {
      if (window.SCISHIELD_DSM_NOTIFY) {
        showNotification(
          `persist-${status}`,
          item.innerText,
          window.location.pathname,
        );
      } else {
        addToHistory(
          `persist-${status}`,
          item.innerText,
          window.location.pathname,
          {},
          Date.now(),
          newSortableRandomId(),
          false,
        );
      }
    });
  }
}

function markAllRead() {
  const modified = history.value
    ? history.value?.map((item) => ({ ...item, unread: false }))
    : [];
  set(modified);
}

const numShown = ref(10);
const listStart = ref(0);
const listEnd = ref(numShown.value - 1);
const onlySticky = true;

const sortedHistory = computed(() => {
  const sorted = history.value
    ? history.value
        .filter((item) =>
          onlySticky
            ? typeof item?.type === "string" && item.type.includes("persist")
            : true,
        )
        .toSorted((a, b) => b.date - a.date)
    : [];
  const limited = sorted.slice(listStart.value, listEnd.value);
  return limited;
});

const unreadItems = computed(() =>
  history.value
    ? history.value?.filter(
        (item) =>
          item.unread &&
          (onlySticky
            ? typeof item?.type === "string" && item.type.includes("persist")
            : true),
      )
    : [],
);

const countTotal = computed(() =>
  history.value
    ? history.value?.filter((item) =>
        onlySticky
          ? typeof item?.type === "string" && item.type.includes("persist")
          : true,
      ).length
    : 0,
);

const countUnread = computed(() => unreadItems.value.length);

const newerHidden = computed(
  () => countTotal.value > numShown.value && listStart.value > 0,
);

const olderHidden = computed(() => countTotal.value > listEnd.value);

const showOlder = () => {
  if (listEnd.value <= countTotal.value) {
    listStart.value += numShown.value;
    listEnd.value += numShown.value;
  }
};

const showNewer = () => {
  if (listStart.value >= numShown.value) {
    listStart.value -= numShown.value;
    listEnd.value -= numShown.value;
  }
};

Vue.prototype.$showNotification = showNotification;
Vue.prototype.$clearNotification = clearNotification;

function newerThanOneWeek(date) {
  return date > Math.floor((Date.now() - 12096e5) / 1000);
}

function removeOlderThanOneWeek() {
  if (history.value) {
    const filtered = history.value?.filter((item) =>
      newerThanOneWeek(item.date),
    );
    if (filtered.length < history.value?.length) {
      history.value = filtered;
      set(filtered);
    }
  }
}

async function fetchPrunedHistory() {
  await getPersistedValue("notificationHistory").then(() => {
    removeOlderThanOneWeek();
  });
}

// eslint-disable-next-line import/prefer-default-export
export function useNotification(options = {}) {
  const instance = getCurrentInstance();
  router = instance?.proxy?.$router;

  dsmCapture();
  toastOptions = { ...toastOptions, ...options };
  return {
    showNotification,
    clearNotification,
    readNotification,
    markAllRead,
    history,
    sortedHistory,
    countUnread,
    countTotal,
    newerHidden,
    olderHidden,
    showOlder,
    showNewer,
    fetchPrunedHistory,
  };
}
